<template>
  <nav
    ref="menuRoot"
    :dir="isRtl === true ? 'rtl' : 'ltr'"
    :class="menuShellClass"
    id="light-theme-menu"
  >
    <div class="light-menu-head">
      <router-link to="/" exact class="light-menu-brand" id="light-menu-brand-link">
        <img :src="displayLogoSrc" alt="Logo" class="light-menu-logo" @error="handleLogoError">
      </router-link>
    </div>

    <div class="light-menu-items">
      <router-link
        v-for="item in menuItems"
        :key="item.key"
        :to="item.to"
        :exact="item.exact === true"
        class="light-menu-item"
        :id="'light-menu-item-' + item.key"
        :title="item.label"
      >
        <span class="light-menu-icon-wrap">
          <Icon :icon="item.icon" width="22" />
        </span>
        <span v-if="isExpanded === true" class="light-menu-label">{{ item.label }}</span>
      </router-link>
    </div>

    <div class="light-menu-foot">
      <button
        type="button"
        class="light-menu-toggle"
        id="light-menu-expand-toggle"
        :title="isExpanded === true ? collapseLabel : expandLabel"
        @click="toggleExpanded"
      >
        <span class="light-menu-icon-wrap light-menu-toggle-icon">
          <Icon :icon="toggleIcon" width="20" />
        </span>
        <span v-if="isExpanded === true" class="light-menu-label">{{ collapseLabel }}</span>
      </button>
    </div>
  </nav>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { applyVendorBrandingFromCurrentOrigin, globalState } from '@/globalState';
import { useI18n } from '@/i18n';

const emit = defineEmits(['expandChange']);

const { t, currentLanguage } = useI18n();
const menuRoot = ref(null);
const logoLoadFailed = ref(false);
const isExpanded = ref(true);

const storageKey = 'light-menu-expanded';

const isRtl = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');

const expandLabel = computed(() => t('lightMenu.expand', 'Expand menu'));
const collapseLabel = computed(() => t('lightMenu.collapse', 'Collapse menu'));

const toggleIcon = computed(() => {
  if (isRtl.value === true) {
    if (isExpanded.value === true) {
      return 'tabler:chevrons-right';
    }
    return 'tabler:chevrons-left';
  }
  if (isExpanded.value === true) {
    return 'tabler:chevrons-left';
  }
  return 'tabler:chevrons-right';
});

const menuItems = computed(() => [
  {
    key: 'dashboard',
    to: '/',
    exact: true,
    icon: 'tabler:layout-dashboard',
    label: t('sidebar.dashboard', 'Dashboard')
  },
  {
    key: 'ai-agents',
    to: '/aiChatAgents',
    exact: false,
    icon: 'tabler:messages',
    label: t('sidebar.aiAgentsTitle', 'AI Agents')
  },
  {
    key: 'usage',
    to: '/organizationUsage',
    exact: false,
    icon: 'tabler:chart-bar',
    label: t('sidebar.organizationUsage', 'Usage')
  },
  {
    key: 'tools',
    to: '/aiAgentsTools',
    exact: false,
    icon: 'tabler:tool',
    label: t('sidebar.aiAgentTools', 'Tools')
  },
  {
    key: 'campaigns',
    to: '/campaigns',
    exact: false,
    icon: 'tabler:megaphone',
    label: t('sidebar.campaigns', 'Campaigns')
  },
  {
    key: 'reports',
    to: '/reports',
    exact: false,
    icon: 'tabler:file-analytics',
    label: t('sidebar.reportsLink', 'Reports')
  }
]);

const menuShellClass = computed(() => {
  if (isExpanded.value === true) {
    if (isRtl.value === true) {
      return 'light-menu light-menu-expanded light-menu-rtl';
    }
    return 'light-menu light-menu-expanded light-menu-ltr';
  }
  if (isRtl.value === true) {
    return 'light-menu light-menu-collapsed light-menu-rtl';
  }
  return 'light-menu light-menu-collapsed light-menu-ltr';
});

const brandLogoSrc = computed(() => {
  const rawLogo = globalState.whitelabel !== undefined && globalState.whitelabel !== null ? globalState.whitelabel.logoUrl : null;
  if (typeof rawLogo !== 'string') {
    return '';
  }
  const trimmedLogo = rawLogo.trim();
  if (trimmedLogo === '' || trimmedLogo === '/logo.svg') {
    return '';
  }
  return trimmedLogo;
});

const shouldUseBrandLogo = computed(() => {
  const brandingEnabled = globalState.whitelabel !== undefined && globalState.whitelabel !== null ? globalState.whitelabel.enabled : undefined;
  return brandLogoSrc.value !== '' && brandingEnabled !== false && logoLoadFailed.value !== true;
});

const displayLogoSrc = computed(() => {
  if (shouldUseBrandLogo.value === true) {
    return brandLogoSrc.value;
  }
  return '/logo.svg';
});

function handleLogoError() {
  if (shouldUseBrandLogo.value === true) {
    logoLoadFailed.value = true;
  }
}

function readStoredExpanded() {
  if (typeof window === 'undefined' || window.localStorage === undefined || window.localStorage === null) {
    return true;
  }
  const stored = window.localStorage.getItem(storageKey);
  if (stored === null) {
    return true;
  }
  return stored === '1';
}

function persistExpanded(value) {
  if (typeof window === 'undefined' || window.localStorage === undefined || window.localStorage === null) {
    return;
  }
  window.localStorage.setItem(storageKey, value === true ? '1' : '0');
}

function emitExpandState() {
  emit('expandChange', isExpanded.value === true);
}

function toggleExpanded() {
  isExpanded.value = isExpanded.value !== true;
  persistExpanded(isExpanded.value);
  emitExpandState();
}

watch(() => [brandLogoSrc.value, globalState.whitelabel !== undefined && globalState.whitelabel !== null ? globalState.whitelabel.enabled : undefined], () => {
  logoLoadFailed.value = false;
});

onMounted(() => {
  applyVendorBrandingFromCurrentOrigin().catch(() => {});
  isExpanded.value = readStoredExpanded();
  emitExpandState();
});
</script>

<style scoped>
.light-menu {
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  transition: width 0.2s ease;
}

.light-menu-ltr {
  left: 0;
  border-right: 1px solid #e4e4e7;
}

.light-menu-rtl {
  right: 0;
  border-left: 1px solid #e4e4e7;
}

.light-menu-collapsed {
  width: 72px;
}

.light-menu-expanded {
  width: 240px;
}

.light-menu-head {
  flex-shrink: 0;
  padding: 20px 16px 16px 16px;
}

.light-menu-brand {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 32px;
}

.light-menu-collapsed .light-menu-brand {
  justify-content: center;
}

.light-menu-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  transition: width 0.2s ease, height 0.2s ease;
}

.light-menu-expanded .light-menu-logo {
  width: 132px;
  height: 32px;
}

.light-menu-items {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 10px;
}

.light-menu-item {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 4px;
  padding: 12px 12px;
  border-radius: 8px;
  color: #52525b;
  font-weight: 500;
  font-size: 15px;
  line-height: 1.35;
  letter-spacing: -0.01em;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.light-menu-item:first-child {
  margin-top: 0;
}

.light-menu-item:hover {
  background: #f4f4f5;
  color: #18181b;
}

.light-menu-item.router-link-active,
.light-menu-item.exact-active {
  background: #f4f4f5;
  color: #18181b;
  font-weight: 500;
}

.light-menu-ltr .light-menu-item.router-link-active::before,
.light-menu-ltr .light-menu-item.exact-active::before {
  content: '';
  position: absolute;
  left: -10px;
  top: 9px;
  bottom: 9px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: #2563eb;
}

.light-menu-rtl .light-menu-item.router-link-active::before,
.light-menu-rtl .light-menu-item.exact-active::before {
  content: '';
  position: absolute;
  right: -10px;
  top: 9px;
  bottom: 9px;
  width: 3px;
  border-radius: 3px 0 0 3px;
  background: #2563eb;
}

.light-menu-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: #71717a;
}

.light-menu-item:hover .light-menu-icon-wrap {
  color: #3f3f46;
}

.light-menu-item.router-link-active .light-menu-icon-wrap,
.light-menu-item.exact-active .light-menu-icon-wrap {
  color: #2563eb;
}

.light-menu-label {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.light-menu-ltr .light-menu-label {
  margin-left: 14px;
  text-align: left;
}

.light-menu-rtl .light-menu-label {
  margin-right: 14px;
  text-align: right;
}

.light-menu-collapsed .light-menu-label {
  display: none;
}

.light-menu-collapsed .light-menu-item {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.light-menu-foot {
  flex-shrink: 0;
  padding: 8px;
  border-top: 1px solid #f4f4f5;
}

.light-menu-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 12px;
  border-radius: 8px;
  color: #a1a1aa;
  font-weight: 500;
  font-size: 14px;
  background: transparent;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.light-menu-toggle:hover {
  background: #f4f4f5;
  color: #52525b;
}

.light-menu-toggle-icon {
  color: inherit;
}

.light-menu-collapsed .light-menu-toggle {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}
</style>
